import React, { useState } from 'react';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  IdentificationIcon,
  UserGroupIcon,
  MapPinIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { AxiosError } from 'axios';
import {
  useLearnerAttributes,
  useCreateLearnerAttribute,
  useUpdateLearnerAttribute,
  useDeleteLearnerAttribute,
} from '../../services/settings';
import { LearnerAttribute, LearnerAttributeKind } from '../../types/settings';

/* ── Attribute Card ──────────────────────────────────────────────────── */
const AttributeCard: React.FC<{
  item: LearnerAttribute;
  kindLabel: string;
  kindIcon: React.ReactNode;
  onEdit: (item: LearnerAttribute) => void;
  onDelete: (item: LearnerAttribute) => void;
}> = ({ item, kindLabel, kindIcon, onEdit, onDelete }) => (
  <div className="bg-surface-primary border border-border-primary/10 rounded-xl p-5 flex flex-col justify-between gap-4">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <h3 className="text-[15px] font-semibold text-text-primary leading-6 truncate">{item.name}</h3>
        {item.description && (
          <p className="text-[13px] text-text-secondary leading-[18px] mt-1 line-clamp-2">{item.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => onEdit(item)}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
          aria-label={`Edit ${kindLabel}`}
        >
          <PencilIcon className="w-4 h-4 text-primary-500" />
        </button>
        <button
          onClick={() => onDelete(item)}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
          aria-label={`Delete ${kindLabel}`}
        >
          <TrashIcon className="w-4 h-4 text-error-500" />
        </button>
      </div>
    </div>

    <div className="flex items-center gap-2 bg-primary-500/[0.08] rounded-[10px] px-3 h-[37px]">
      <span className="text-primary-500 shrink-0" aria-hidden="true">
        {kindIcon}
      </span>
      <span className="text-[14px] font-semibold text-primary-600 dark:text-primary-350">
        {kindLabel}
      </span>
    </div>
  </div>
);

/* ── Add / Edit modal ────────────────────────────────────────────────── */
const AttributeModal: React.FC<{
  kindLabel: string;
  initial?: LearnerAttribute;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
  isSaving: boolean;
  error: string | null;
}> = ({ kindLabel, initial, onClose, onSave, isSaving, error }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-surface-primary rounded-2xl border border-border-primary w-full max-w-md p-6 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-text-primary">
            {initial ? `Edit ${kindLabel}` : `Add ${kindLabel}`}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800">
            <XMarkIcon className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2.5 bg-error-50 dark:bg-error-900/20 rounded-lg text-sm text-error-600 dark:text-error-400">
            <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="relative">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder=" "
              className="peer w-full h-14 px-3.5 pt-5 pb-2 text-sm text-text-primary bg-surface-primary dark:bg-surface-elevated border border-border-primary/20 focus:border-primary-500 rounded-xl outline-none transition-colors"
            />
            <label className="absolute left-3.5 top-1.5 text-xs font-medium text-text-secondary pointer-events-none">
              {kindLabel} Name
            </label>
          </div>
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder=" "
              className="peer w-full px-3.5 pt-5 pb-2 text-sm text-text-primary bg-surface-primary dark:bg-surface-elevated border border-border-primary/20 focus:border-primary-500 rounded-xl outline-none transition-colors resize-none"
            />
            <label className="absolute left-3.5 top-1.5 text-xs font-medium text-text-secondary pointer-events-none">
              Description (optional)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-text-secondary border border-border-primary/20 rounded-full hover:bg-neutral-50 dark:hover:bg-grey-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (name.trim()) onSave(name.trim(), description.trim()); }}
            disabled={!name.trim() || isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 rounded-full transition-colors disabled:opacity-50"
          >
            <CheckIcon className="w-4 h-4" />
            {isSaving ? 'Saving…' : initial ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Delete confirm ──────────────────────────────────────────────────── */
const DeleteConfirm: React.FC<{
  item: LearnerAttribute;
  kindLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  error: string | null;
}> = ({ item, kindLabel, onClose, onConfirm, isDeleting, error }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
    <div className="bg-surface-primary rounded-2xl border border-border-primary w-full max-w-sm p-6 flex flex-col gap-4">
      <h2 className="text-lg font-bold text-text-primary">Delete {kindLabel}</h2>
      <p className="text-sm text-text-secondary">
        Are you sure you want to delete{' '}
        <span className="font-semibold text-text-primary">{item.name}</span>? This action cannot be undone.
      </p>
      {(item.learner_count ?? 0) > 0 && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-warning-50 dark:bg-warning-900/20 rounded-lg text-sm text-warning-800 dark:text-warning-200">
          <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
          {item.learner_count} member{item.learner_count === 1 ? ' has' : 's have'} this{' '}
          {kindLabel.toLowerCase()} on their profile. Deleting is blocked until you reassign{' '}
          {item.learner_count === 1 ? 'them' : 'them all'} — nothing is ever removed from a
          profile silently.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-error-50 dark:bg-error-900/20 rounded-lg text-sm text-error-600 dark:text-error-400">
          <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-1">
        <button
          onClick={onClose}
          disabled={isDeleting}
          className="px-4 py-2 text-sm font-medium text-text-secondary border border-border-primary/20 rounded-full hover:bg-neutral-50 dark:hover:bg-grey-800 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 py-2 text-sm font-bold text-white bg-error-500 hover:bg-error-600 rounded-full transition-colors disabled:opacity-50"
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

/* ── Generic Attribute View ──────────────────────────────────────────── */
const AttributeView: React.FC<{
  kind: LearnerAttributeKind;
  kindLabel: string;        // e.g. "Department"
  kindIcon: React.ReactNode;
  pluralLabel: string;      // e.g. "Departments"
  analyticsTitle: string;   // e.g. "Departments Analytics"
}> = ({ kind, kindLabel, kindIcon, pluralLabel, analyticsTitle }) => {
  const { data, isLoading, isError } = useLearnerAttributes(kind);
  const createMutation = useCreateLearnerAttribute(kind);
  const updateMutation = useUpdateLearnerAttribute(kind);
  const deleteMutation = useDeleteLearnerAttribute(kind);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LearnerAttribute | null>(null);
  const [deleting, setDeleting] = useState<LearnerAttribute | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const items = data?.data ?? [];
  const total = data?.meta.total ?? items.length;

  const extractErrorMessage = (err: unknown): string => {
    if (err instanceof AxiosError) {
      const body = err.response?.data as { error?: string; message?: string } | undefined;
      if (body?.error === 'ATTRIBUTE_IN_USE') return body.message ?? 'This item is in use and cannot be deleted.';
      if (body?.error === 'DUPLICATE_NAME') return body.message ?? 'A record with this name already exists.';
      if (body?.message) return body.message;
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const handleAdd = (name: string, description: string) => {
    setModalError(null);
    createMutation.mutate(
      { name, description: description || null },
      {
        onSuccess: () => setAdding(false),
        onError: (err) => setModalError(extractErrorMessage(err)),
      }
    );
  };

  const handleEdit = (name: string, description: string) => {
    if (!editing) return;
    setModalError(null);
    updateMutation.mutate(
      { id: editing.id, data: { name, description: description || null } },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => setModalError(extractErrorMessage(err)),
      }
    );
  };

  const handleDelete = () => {
    if (!deleting) return;
    setDeleteError(null);
    deleteMutation.mutate(deleting.id, {
      onSuccess: () => setDeleting(null),
      onError: (err) => setDeleteError(extractErrorMessage(err)),
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-text-secondary">
        <ExclamationCircleIcon className="w-8 h-8 text-error-400" />
        <p className="text-sm">Failed to load {pluralLabel.toLowerCase()}.</p>
      </div>
    );
  }

  return (
    <>
      {/* Analytics card */}
      <div className="bg-grey-100 dark:bg-grey-800/50 rounded-2xl p-4 mb-6">
        <h3 className="text-base font-semibold text-text-primary mb-3">{analyticsTitle}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-[13px] text-text-secondary">Total Count</p>
            <p className="text-2xl font-bold text-text-primary leading-9">{total}</p>
          </div>
          <div>
            <p className="text-[13px] text-text-secondary">Showing</p>
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-350 leading-9">{items.length}</p>
          </div>
        </div>
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-text-primary pl-4">All {pluralLabel}</h2>
        <button
          onClick={() => { setModalError(null); setAdding(true); }}
          className="flex items-center gap-2 h-[37px] px-4 bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold rounded-full transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add New
        </button>
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-text-secondary">
          <p className="text-sm">No {pluralLabel.toLowerCase()} yet.</p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <AttributeCard
            key={item.id}
            item={item}
            kindLabel={kindLabel}
            kindIcon={kindIcon}
            onEdit={(i) => { setModalError(null); setEditing(i); }}
            onDelete={(i) => { setDeleteError(null); setDeleting(i); }}
          />
        ))}
      </div>

      {/* Modals */}
      {adding && (
        <AttributeModal
          kindLabel={kindLabel}
          onClose={() => setAdding(false)}
          onSave={handleAdd}
          isSaving={createMutation.isPending}
          error={modalError}
        />
      )}
      {editing && (
        <AttributeModal
          kindLabel={kindLabel}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={handleEdit}
          isSaving={updateMutation.isPending}
          error={modalError}
        />
      )}
      {deleting && (
        <DeleteConfirm
          item={deleting}
          kindLabel={kindLabel}
          onClose={() => setDeleting(null)}
          onConfirm={handleDelete}
          isDeleting={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </>
  );
};

/* ── Main TeamSettings ───────────────────────────────────────────────── */
type OrgTab = LearnerAttributeKind;

const TAB_META: {
  id: OrgTab;
  label: string;
  plural: string;
  analyticsTitle: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'roles',
    label: 'Role',
    plural: 'Roles',
    analyticsTitle: 'Roles Analytics',
    icon: <IdentificationIcon className="w-4 h-4" />,
  },
  {
    id: 'departments',
    label: 'Department',
    plural: 'Departments',
    analyticsTitle: 'Departments Analytics',
    icon: <UserGroupIcon className="w-4 h-4" />,
  },
  {
    id: 'locations',
    label: 'Location',
    plural: 'Locations',
    analyticsTitle: 'Locations Analytics',
    icon: <MapPinIcon className="w-4 h-4" />,
  },
];

const TeamSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<OrgTab>(TAB_META[0].id);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const activeMeta = TAB_META.find((t) => t.id === activeTab)!;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* ── Mobile: dropdown selector ─────────────────────────────── */}
      <div className="lg:hidden relative">
        <button
          onClick={() => setMobileDropdownOpen((v) => !v)}
          className="w-full flex items-center justify-between h-14 px-3.5 text-[15px] text-text-secondary bg-surface-primary dark:bg-surface-elevated border border-border-primary/20 rounded-full"
        >
          <span>{activeMeta.plural}</span>
          <ChevronDownIcon className={`w-5 h-5 transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileDropdownOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setMobileDropdownOpen(false)} />
            <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 bg-surface-primary border border-border-primary/40 rounded-xl overflow-hidden">
              {TAB_META.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileDropdownOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-primary-600 dark:text-primary-350 bg-primary-50 dark:bg-primary-900/20'
                      : 'text-text-secondary hover:bg-neutral-50 dark:hover:bg-grey-800'
                  }`}
                >
                  {tab.icon}
                  {tab.plural}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Desktop: side nav ─────────────────────────────────────── */}
      <nav className="hidden lg:flex flex-col gap-1 w-[160px] shrink-0 pt-1">
        {TAB_META.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-full text-sm font-medium text-left transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 dark:text-primary-350'
                : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-grey-800'
            }`}
          >
            {tab.icon}
            {tab.plural}
          </button>
        ))}
      </nav>

      {/* ── Content ───────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        <AttributeView
          key={activeTab}
          kind={activeTab}
          kindLabel={activeMeta.label}
          kindIcon={activeMeta.icon}
          pluralLabel={activeMeta.plural}
          analyticsTitle={activeMeta.analyticsTitle}
        />
      </div>
    </div>
  );
};

export default TeamSettings;
